import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../../styles/LectorTemas.css';
import { ProtectedAudio, ProtectedImage, ProtectedVideo, InlinePdf } from './ProtectedMedia';
import { FlashcardsDeck } from './FlashcardsDeck';
import { getLessonFlashcards } from '../../utils/courseStructure';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * Lector de Temas Vortex Academy
 * Maqueta React del prototipo aprobado. Recibe `lessons` como props
 * (esquema: paragraphs, bullets, callout, kps, quizQuestions, isQuiz)
 * para validación visual; luego se conectará a RTDB
 * (vortex_lector_temas/{jerarquia}/{cuerpo}/{especialidad}/{temaId}).
 */
export const LectorTemas = ({ courseTitle = '', moduleLabel = '', lessons = [], onFinish, initialCompleted = {}, onLessonComplete, hasFinalExam = false, onFinalExam, onActiveLessonChange }) => {
  const readerRef = useRef(null);
  const toastTimer = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completed, setCompleted] = useState({ ...initialCompleted });
  const [openKp, setOpenKp] = useState({});
  const [answered, setAnswered] = useState({});
  const [toast, setToast] = useState(null);
  const [readingPct, setReadingPct] = useState(0);

  const total = lessons.length;
  const current = lessons[currentIndex] || null;
  const completedCount = useMemo(
    () => lessons.filter((l) => completed[l.id] || l.completed).length,
    [completed, lessons]
  );
  const progressPct = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  // Al cambiar de tema (lessons) restablecer el índice y cargar el avance guardado
  const lessonsKey = lessons.map((l) => l.id).join('|');
  useEffect(() => {
    setCurrentIndex(0);
    const init = { ...initialCompleted };
    lessons.forEach((l) => { if (l.completed) init[l.id] = true; });
    setCompleted(init);
  }, [lessonsKey]);

  // Al cambiar de lección: cerrar/abrir KP por defecto, resetear lectura
  useEffect(() => {
    const kps = current?.kps || [];
    setOpenKp((prev) => {
      const next = { ...prev };
      kps.forEach((_, i) => {
        const key = `${current.id}:${i}`;
        if (next[key] === undefined) next[key] = i === 0;
      });
      return next;
    });
    setReadingPct(0);
    readerRef.current?.scrollTo({ top: 0 });
    if (onActiveLessonChange) onActiveLessonChange(current);
  }, [currentIndex]);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2800);
  };

  const goTo = (i) => {
    if (i < 0 || i >= total) return;
    setCurrentIndex(i);
  };

  // Una lección se puede superar solo si su autoevaluación rápida está
  // completa y TODAS las respuestas son correctas. Si la lección no tiene
  // autoevaluación, basta con haberla leído (siempre se puede avanzar).
  const lessonQuizCount = (lesson) => (lesson.quizQuestions || []).filter((q) => q && q.question && (q.options || []).length > 1).length;
  const lessonAllCorrect = (lesson) => (lesson.quizQuestions || []).every((q, qi) => {
    const k = `${lesson.id}:${qi}`;
    return answered[k] !== undefined && answered[k] === q.correct_index;
  });
  const canAdvance = (lesson) => lessonQuizCount(lesson) === 0 || lessonAllCorrect(lesson);

  const goNext = () => {
    if (!current) return;
    if (!canAdvance(current)) {
      showToast('Responde correctamente toda la autoevaluación rápida para continuar.', false);
      return;
    }
    if (currentIndex === total - 1) {
      // Última lección: si el tema tiene examen final de módulo (banco RTDB),
      // el botón lleva al examen en vez de "finalizar curso".
      if (hasFinalExam) {
        setCompleted((prev) => ({ ...prev, [current.id]: true }));
        if (onLessonComplete) onLessonComplete(current.id);
        if (onFinalExam) onFinalExam();
        return;
      }
      showToast('🎉 ¡Curso completado!');
      if (onFinish) onFinish();
      return;
    }
    setCompleted((prev) => ({ ...prev, [current.id]: true }));
    if (onLessonComplete) onLessonComplete(current.id);
    goTo(currentIndex + 1);
  };

  const toggleKp = (key) => setOpenKp((prev) => ({ ...prev, [key]: !prev[key] }));

  const answerQuestion = (lessonId, qIdx, optIdx, correctIdx) => {
    const key = `${lessonId}:${qIdx}`;
    const prev = answered[key];
    if (prev === correctIdx) return;
    setAnswered((prev) => ({ ...prev, [key]: optIdx }));
    if (optIdx === correctIdx) {
      showToast('✅ ¡Correcto!');
    } else {
      showToast('✘ Incorrecta, inténtalo de nuevo.', false);
    }
  };

  /* ── Render de material de apoyo (video + PDFs) ── */
  const toEmbedUrl = (url = '') => {
    const t = url.trim();
    if (!t) return '';
    if (t.includes('youtu.be')) return t.replace('youtu.be/', 'youtube.com/embed/').split('?')[0];
    const watch = t.match(/youtube\.com\/watch\?v=([\w-]+)/);
    if (watch) return `https://www.youtube.com/embed/${watch[1]}`;
    const embed = t.match(/youtube\.com\/embed\/([\w-]+)/);
    if (embed) return `https://www.youtube.com/embed/${embed[1]}`;
    return t;
  };

  const renderResources = (lesson) => {
    const videoUrl = toEmbedUrl(lesson.video_url);
    const audioUrl = lesson.audio_url || '';
    const infographicUrl = lesson.infographic_url || '';
    const slidesUrl = lesson.slides_url || '';
    const images = (Array.isArray(lesson.images) ? lesson.images : []).filter((im) => im && im.url);
    const pdfs = Array.isArray(lesson.pdf_resources) ? lesson.pdf_resources.filter((p) => p && p.url) : [];
    if (!videoUrl && !audioUrl && !infographicUrl && !slidesUrl && images.length === 0 && pdfs.length === 0) return null;
    const isYoutube = videoUrl.includes('youtube.com/embed');
    return (
      <div className="lt-resources">
        <h3>📎 Material de apoyo</h3>
        {videoUrl && (
          <div className="lt-resource-block">
            <span className="lt-tag">● Video</span>
            {isYoutube ? (
              <div className="lt-video-wrap">
                <iframe
                  src={videoUrl}
                  title="Video de apoyo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <ProtectedVideo className="lt-video" src={videoUrl} />
            )}
          </div>
        )}
        {audioUrl && (
          <div className="lt-resource-block">
            <span className="lt-tag">● Podcast / audio</span>
            {lesson.audio_caption && (
              <p style={{ margin: '6px 0 4px', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary, #fff)' }}>
                {lesson.audio_caption}
              </p>
            )}
            <ProtectedAudio src={audioUrl} style={{ width: '100%', marginTop: '8px' }} />
          </div>
        )}
        {infographicUrl && (
          <div className="lt-resource-block">
            <span className="lt-tag">● Infografía (solo lectura)</span>
            <ProtectedImage src={infographicUrl} alt="Infografía de la lección" caption="Infografía · solo lectura" />
          </div>
        )}
        {slidesUrl && (
          <div className="lt-resource-block">
            <span className="lt-tag">● Presentación de diapositivas (solo lectura)</span>
            <InlinePdf url={slidesUrl} name="Presentación de diapositivas (solo lectura)" />
          </div>
        )}
        {images.length > 0 && (
          <div className="lt-resource-block">
            <span className="lt-tag">● Imágenes de apoyo</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {images.map((im, i) => (
                <ProtectedImage key={im.id || i} src={im.url} alt={`Imagen de apoyo ${i + 1}`} caption={im.caption} />
              ))}
            </div>
          </div>
        )}
        {pdfs.length > 0 && (
          <div className="lt-resource-block">
            <span className="lt-tag">● Documentos PDF (solo lectura)</span>
            <div className="lt-pdf-list">
              {pdfs.map((pdf, i) => (
                <InlinePdf key={pdf.id || i} url={pdf.url} name={pdf.name || `Documento ${i + 1}`} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleScroll = () => {
    const el = readerRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    setReadingPct(max > 0 ? Math.round((el.scrollTop / max) * 100) : 0);
  };

  /* ── Render de preguntas (autoevaluación o evaluación de módulo) ── */
  const renderQuizQuestions = (lesson, tagLabel) => {
    if (!lesson.quizQuestions || lesson.quizQuestions.length === 0) return null;
    return (
      <div className="lt-interactive">
        <span className="lt-tag">● {tagLabel}</span>
        {lesson.quizQuestions.map((q, qIdx) => {
          const key = `${lesson.id}:${qIdx}`;
          const selected = answered[key];
          const locked = selected !== undefined && selected === q.correct_index;
          return (
            <div key={key} className="lt-q-block">
              <div className="lt-q">{qIdx + 1}. {q.question}</div>
              {q.options.map((opt, optIdx) => {
                const isSelected = selected === optIdx;
                const isCorrect = locked && q.correct_index === optIdx;
                const isWrong = locked && isSelected && !isCorrect;
                let cls = 'lt-q-opt';
                if (isCorrect) cls += ' correct';
                else if (isWrong) cls += ' wrong';
                else if (isSelected) cls += ' selected';
                if (locked) cls += ' disabled';
                return (
                  <button
                    key={optIdx}
                    type="button"
                    className={cls}
                    onClick={() => answerQuestion(lesson.id, qIdx, optIdx, q.correct_index)}
                    disabled={locked}
                  >
                    <span className="lt-letter">{LETTERS[optIdx]}</span>
                    <span>{opt}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  /* ── Render del artículo de lectura ── */
  const renderArticle = (lesson) => {
    const isQuiz = !!lesson.isQuiz;
    if (isQuiz) {
      return (
        <div className="lt-article">
          {lesson.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          {renderQuizQuestions(lesson, 'Evaluación interactiva')}
        </div>
      );
    }
    return (
      <div className="lt-article">
        {lesson.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        {lesson.bullets && lesson.bullets.length > 0 && (
          <>
            <h3>🗂️ Conceptos clave</h3>
            <ul>{lesson.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
          </>
        )}
        {lesson.callout && (
          <div className={`lt-callout ${lesson.callout.type}`}>
            <span className="lt-ico">{lesson.callout.icon}</span>
            <span>{lesson.callout.text}</span>
          </div>
        )}
        {lesson.kps && lesson.kps.length > 0 && (
          <>
            <h3>💡 Puntos clave · toca para expandir</h3>
            {lesson.kps.map((k, i) => {
              const key = `${lesson.id}:${i}`;
              return (
                <div key={key} className={`lt-kp-card${openKp[key] ? ' open' : ''}`} onClick={() => toggleKp(key)}>
                  <div className="lt-head">
                    <span>{k.t}</span>
                    <span className="lt-chev">▾</span>
                  </div>
                  <div className="lt-body"><p>{k.b}</p></div>
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  /* ── Render del índice lateral ── */
  const renderSidebar = () => (
    <aside className="lt-sidebar">
      <div className="lt-sidebar-header">
        <h2>{courseTitle}</h2>
        <p>{moduleLabel}</p>
        <div className="lt-progress-overview">
          <div className="lt-bar"><div style={{ width: `${progressPct}%` }} /></div>
          <span>{completedCount} de {total} lecciones completadas</span>
        </div>
      </div>
      <div className="lt-lesson-list">
        {lessons.map((l, i) => {
          const isDone = !!completed[l.id] || !!l.completed;
          const isCurrent = i === currentIndex;
          const isQuiz = !!l.isQuiz;
          let dot = <div className="lt-status-dot locked">🔒</div>;
          if (isDone) dot = <div className="lt-status-dot done">✓</div>;
          else if (isCurrent) dot = <div className="lt-status-dot current">{i + 1}</div>;
          const icon = isQuiz ? '📝 Evaluación' : (isDone ? '📖 Leído' : '📖 Leer');
          const meta = isQuiz ? `${l.quizQuestions?.length || 0} preguntas` : '~6 min';
          return (
  <div
  key={l.id}
  className={`lt-lesson-item${isCurrent ? ' active' : ''}${isDone ? ' completed' : ''}`}
  role="button"
  tabIndex={0}
  onClick={() => goTo(i)}
  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goTo(i); } }}
  >
              {dot}
              <div>
                <div className="lt-title">{l.title}</div>
                <div className="lt-meta"><span>{icon}</span><span>{meta}</span></div>
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );

  /* ── Render del panel de lectura ── */
  const renderReader = () => {
    if (!current) {
      return (
        <div className="lt-reader">
          <div className="lt-reader-inner">
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>No hay lecciones en este tema.</p>
          </div>
        </div>
      );
    }
    const isQuiz = !!current.isQuiz;
    const isDone = !!completed[current.id] || !!current.completed;
    const metaText = isQuiz
      ? null
      : `${current.kps?.length || 0} puntos clave · ${current.interactive ? '1 quiz interactivo' : 'lectura'}`;
    return (
      <div className="lt-reader" ref={readerRef} onScroll={handleScroll}>
        <div className="lt-reader-inner">
          <div className="lt-breadcrumb">
            <span>Inicio</span><span className="lt-sep">›</span>
            <span>{current.module || moduleLabel}</span><span className="lt-sep">›</span>
            <span className="lt-current">{current.title.split(':')[0]}</span>
          </div>
          <h1 className="lt-lesson-title">{current.title}</h1>
          <div className="lt-lesson-sub">
            {isQuiz ? (
              <span className="lt-pill" style={{ background: 'rgba(197,160,89,0.15)', borderColor: 'rgba(197,160,89,0.35)', color: 'var(--gold-light)' }}>
                📝 Evaluación del módulo
              </span>
            ) : (
              <>
                <span className="lt-pill">{isDone ? '✔ Completada' : '○ Pendiente'}</span>
                <span className="lt-pill dark">📖 Artículo de lectura</span>
                <span>{metaText}</span>
              </>
            )}
          </div>
          {renderArticle(current)}
          {renderResources(current)}
          {(() => {
            const lessonFlashcards = getLessonFlashcards(current);
            if (lessonFlashcards.length === 0) return null;
            return (
              <div className="lt-resources">
                <h3>🗂️ Flashcards de esta lección</h3>
                <FlashcardsDeck flashcards={lessonFlashcards} />
              </div>
            );
          })()}
          {!isQuiz && renderQuizQuestions(current, 'Autoevaluación rápida')}
          <div className="lt-reader-nav">
            <button type="button" className="lt-nav-btn" onClick={() => goTo(currentIndex - 1)} disabled={currentIndex === 0}>
              ← Anterior
            </button>
            <button type="button" className="lt-nav-btn primary" onClick={goNext} disabled={!canAdvance(current)}>
              {currentIndex === total - 1
                ? (hasFinalExam ? '🏆 Examen del módulo' : '🎉 Finalizar curso')
                : 'Siguiente →'}
            </button>
            {!canAdvance(current) && (
              <span style={{ display: 'block', marginTop: '10px', fontSize: '0.75rem', color: '#fb923c' }}>
                Responde correctamente toda la autoevaluación para continuar.
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (total === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0a1a2a' }}>
        <p style={{ fontSize: '0.9rem', color: '#94a3b8' }}>Sin lecciones para mostrar.</p>
      </div>
    );
  }

  return (
    <div className="lt-app">
      <div className="lt-reading-progress" style={{ width: `${readingPct}%` }} />
      {renderSidebar()}
      {renderReader()}
      {toast && (
        <div className={`lt-toast${toast.ok ? '' : ' error'} show`}>{toast.msg}</div>
      )}
    </div>
  );
};
